import React, { useEffect, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, SkipForward, SkipBack, Loader2, Music2, Check, X } from 'lucide-react'
import { MultiTrackMixPlayer } from '@/components/MultiTrackMixPlayer'
import { MixPairNavigator } from '@/components/MixPairNavigator'
import { MixtabilityDisplay } from '@/components/MixtabilityDisplay'
import { StatusLabel } from '@/components/StatusLabel'
import { DebugStatus } from '@/components/DebugStatus'

// 新的数据结构类型定义
interface NewTrack {
  hashName: string
  artistName: string
  trackName: string
  bpm: number
  duration_seconds: number
  syncIn?: {
    startTime: number
    endTime: number
    startBeat: number
    nBeats: number
  }
  syncOut?: {
    startTime: number
    endTime: number
    startBeat: number
    nBeats: number
  }
}

interface NewMixStatus {
  statusCode: number
  errorMessage: string
  bpmDifferencePercent: number
  automationPreset: string
  mixSituationName: string
  mixtability: {
    bpmDiffPercent: {
      score: number
      threshold: number
      isPass: boolean
    }
    pickedCandidateIndex: number
    candidates: Array<{
      nbeats: number
      startbeat1: number
      startbeat2: number
      score: number
      factorsPass: boolean
      scoreFactors: Array<{
        name: string
        score: number
        threshold: number
        isPass: boolean
      }>
    }>
  }
}

interface NewMixPair {
  index: number
  debug_status: number
  debug_msg: string
  track_prev: NewTrack
  track_next: NewTrack
  mixStatus: NewMixStatus
  syncout_length_samples: number
  syncout_length_seconds: number
  presyncout_length_samples: number
  presyncout_length_seconds: number
}

interface NewMixinfo {
  sequence_id: string
  playlist_statistics: {
    totalMixPairs: number
    successfulMixes: number
    failedMixes: number
    successRate: number
    [key: string]: any
  }
  mix_pairs: NewMixPair[]
}

// 向后兼容的旧数据结构类型定义
interface Track {
  artist: string
  song: string
  hash: string
  bpm: number
  duration_seconds?: number
  sync_in?: {
    starttime: number
    endtime: number
    startbeat: number
    nbeats: number
  }
  sync_out?: {
    starttime: number
    endtime: number
    startbeat: number
    nbeats: number
  }
}

interface MixPair {
  pair_index: number
  mix_situation_name: string
  is_success: boolean
  error_code: number
  error_message: string
  // 添加新的statusCode字段支持
  statusCode?: number
  // 添加debug字段支持
  debug_status?: number
  debug_msg?: string
  mixtability: {
    bpm: {
      difference_percent: number
      threshold: number
      pass: boolean
    }
    genre: {
      compatibility_score: number
      compatibility_reason: string
      pass: boolean
    }
    score_factors: Array<{
      name: string
      score: number
      threshold: number
      pass: boolean
      description: string
    }>
    overall: {
      pass: boolean
      reason: string
      score: number
    }
  }
  bpm_difference_percent: number
  transition_nbeats: number
  automation_preset: string
  track_prev: Track
  track_next: Track
  merge_info: {
    presyncout_length_samples: number
    presyncout_length_seconds: number
    syncout_length_samples: number
    syncout_length_seconds: number
  }
}

// 数据适配器函数
function adaptNewTrackToOld(newTrack: NewTrack): Track {
  return {
    artist: newTrack.artistName,
    song: newTrack.trackName,
    hash: newTrack.hashName,
    bpm: newTrack.bpm,
    duration_seconds: newTrack.duration_seconds,
    sync_in: newTrack.syncIn ? {
      starttime: newTrack.syncIn.startTime,
      endtime: newTrack.syncIn.endTime,
      startbeat: newTrack.syncIn.startBeat,
      nbeats: newTrack.syncIn.nBeats
    } : undefined,
    sync_out: newTrack.syncOut ? {
      starttime: newTrack.syncOut.startTime,
      endtime: newTrack.syncOut.endTime,
      startbeat: newTrack.syncOut.startBeat,
      nbeats: newTrack.syncOut.nBeats
    } : undefined
  }
}

function adaptNewMixPairToOld(newPair: NewMixPair): MixPair {
  const pickedCandidate = newPair.mixStatus.mixtability.candidates[newPair.mixStatus.mixtability.pickedCandidateIndex] || newPair.mixStatus.mixtability.candidates[0]
  
  return {
    pair_index: newPair.index,
    mix_situation_name: newPair.mixStatus.mixSituationName,
    is_success: newPair.mixStatus.statusCode === 0,
    error_code: newPair.mixStatus.statusCode,
    error_message: newPair.mixStatus.errorMessage,
    statusCode: newPair.mixStatus.statusCode, // 添加statusCode字段
    debug_status: newPair.debug_status, // 保留debug_status字段
    debug_msg: newPair.debug_msg, // 保留debug_msg字段
    mixtability: {
      bpm: {
        difference_percent: newPair.mixStatus.bpmDifferencePercent,
        threshold: newPair.mixStatus.mixtability.bpmDiffPercent.threshold,
        pass: newPair.mixStatus.mixtability.bpmDiffPercent.isPass
      },
      genre: {
        compatibility_score: 0, // 新结构中没有这个字段，使用默认值
        compatibility_reason: 'Auto-generated compatibility',
        pass: true
      },
      score_factors: pickedCandidate ? pickedCandidate.scoreFactors.map(factor => ({
        name: factor.name,
        score: factor.score,
        threshold: factor.threshold,
        pass: factor.isPass,
        description: factor.name // 使用名称作为描述
      })) : [],
      overall: {
        pass: pickedCandidate ? pickedCandidate.factorsPass : false,
        reason: newPair.mixStatus.errorMessage,
        score: pickedCandidate ? pickedCandidate.score : 0
      }
    },
    bpm_difference_percent: newPair.mixStatus.bpmDifferencePercent,
    transition_nbeats: pickedCandidate ? pickedCandidate.nbeats : 0,
    automation_preset: newPair.mixStatus.automationPreset,
    track_prev: adaptNewTrackToOld(newPair.track_prev),
    track_next: adaptNewTrackToOld(newPair.track_next),
    merge_info: {
      presyncout_length_samples: newPair.presyncout_length_samples,
      presyncout_length_seconds: newPair.presyncout_length_seconds,
      syncout_length_samples: newPair.syncout_length_samples,
      syncout_length_seconds: newPair.syncout_length_seconds
    }
  }
}

function adaptNewMixinfoToOld(newMixinfo: NewMixinfo) {
  return {
    mix_pairs: newMixinfo.mix_pairs.map(adaptNewMixPairToOld),
    session_id: newMixinfo.sequence_id,
    sequencing_method: 'auto' // 默认值，因为新结构中没有这个字段
  }
}

interface QueueDetails {
  id: string
  name: string
  category: string
  instance: string
  tracks: Array<{ filename: string; hash: string; url: string }>
  transitions: Array<{ filename: string; url: string }>
  transitionsMerged?: Array<{ filename: string; url: string }>
  mixinfo: {
    mix_pairs: MixPair[]
    session_id: string
    sequencing_method: string
  } | null
  trackCount: number
  transitionCount: number
  transitionsMergedCount?: number
}

// 时间格式化函数：将秒数转换为 mm:ss 格式
const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = Math.floor(seconds % 60)
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
}

// 时间段可视化组件
interface TimelineBarProps {
  track: Track
  syncType: 'syncIn' | 'syncOut'
  className?: string
}

const TimelineBar: React.FC<TimelineBarProps> = ({ track, syncType, className = "" }) => {
  if (!track.duration_seconds) {
    return null
  }

  const syncData = syncType === 'syncIn' ? track.sync_in : track.sync_out
  if (!syncData) {
    return null
  }

  // 计算sync时间段在整个track duration中的位置和长度
  const startPercent = (syncData.starttime / 1000) / track.duration_seconds * 100
  const endPercent = (syncData.endtime / 1000) / track.duration_seconds * 100
  const widthPercent = endPercent - startPercent

  // 确保百分比在合理范围内
  const clampedStartPercent = Math.max(0, Math.min(100, startPercent))
  const clampedWidthPercent = Math.max(0, Math.min(100 - clampedStartPercent, widthPercent))

  return (
    <div className={`mt-2 ${className}`}>
      <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
        <span>{syncType === 'syncIn' ? 'Sync In' : 'Sync Out'} 时间段</span>
        <span>
          {formatTime(syncData.starttime / 1000)} - {formatTime(syncData.endtime / 1000)}
        </span>
      </div>
      <div className="relative h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        {/* 整体背景条 */}
        <div className="absolute inset-0 bg-gray-300 dark:bg-gray-600"></div>
        {/* sync时间段高亮 */}
        <div 
          className="absolute h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
          style={{
            left: `${clampedStartPercent}%`,
            width: `${clampedWidthPercent}%`
          }}
        ></div>
      </div>
      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-500 mt-1">
        <span>0:00</span>
        <span>{formatTime(track.duration_seconds)}</span>
      </div>
    </div>
  )
}

export const MixPlayerPage: React.FC = () => {
  const { instance, queue, pairIndex: urlPairIndex } = useParams<{ 
    instance: string
    queue: string 
    pairIndex?: string
  }>()
  const navigate = useNavigate()
  
  const [queueDetails, setQueueDetails] = useState<QueueDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentPairIndex, setCurrentPairIndex] = useState(0)
  const [jumpToInput, setJumpToInput] = useState('')

  useEffect(() => {
    if (!instance || !queue) {
      navigate('/')
      return
    }

    const fetchQueueDetails = async () => {
      try {
        const response = await fetch(`/api/experiments/${instance}/${queue}`)
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('播放队列不存在')
          }
          throw new Error('Failed to fetch queue details')
        }
        const data = await response.json()
        
        // 适配新的 mixinfo 数据结构
        if (data.mixinfo && 'sequence_id' in data.mixinfo) {
          // 新格式数据，需要转换为旧格式以保持兼容性
          console.log('检测到新格式 mixinfo 数据，正在转换...')
          data.mixinfo = adaptNewMixinfoToOld(data.mixinfo as NewMixinfo)
          console.log('数据转换完成')
        }
        
        setQueueDetails(data)
        
        // 根据URL参数设置初始的pairIndex (URL使用1-based，内部使用0-based)
        if (urlPairIndex && data.mixinfo && data.mixinfo.mix_pairs) {
          const pairIndexNum = parseInt(urlPairIndex, 10)
          // URL是1-based，转换为0-based内部索引
          const internalIndex = pairIndexNum - 1
          if (!isNaN(pairIndexNum) && pairIndexNum >= 1 && internalIndex < data.mixinfo.mix_pairs.length) {
            setCurrentPairIndex(internalIndex)
          } else {
            // 如果URL中的pairIndex无效，重定向到第一个pair (URL使用1)
            navigate(`/experiments/${instance}/${queue}/1`, { replace: true })
          }
        } else if (!urlPairIndex) {
          // 如果URL中没有pairIndex，重定向到第一个pair (URL使用1)
          navigate(`/experiments/${instance}/${queue}/1`, { replace: true })
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error')
      } finally {
        setLoading(false)
      }
    }

    fetchQueueDetails()
  }, [instance, queue, navigate])

  // 处理URL参数变化（浏览器前进后退或直接修改URL）
  useEffect(() => {
    if (queueDetails && queueDetails.mixinfo && urlPairIndex) {
      const pairIndexNum = parseInt(urlPairIndex, 10)
      const internalIndex = pairIndexNum - 1 // URL是1-based，转换为0-based内部索引
      
      if (!isNaN(pairIndexNum) && pairIndexNum >= 1 && internalIndex < queueDetails.mixinfo.mix_pairs.length) {
        if (currentPairIndex !== internalIndex) {
          setCurrentPairIndex(internalIndex)
        }
      }
    }
  }, [urlPairIndex, queueDetails, currentPairIndex])

  const handlePrevious = () => {
    if (currentPairIndex > 0) {
      const newIndex = currentPairIndex - 1
      setCurrentPairIndex(newIndex)
      // 转换为1-based URL
      navigate(`/experiments/${instance}/${queue}/${newIndex + 1}`, { replace: true })
    }
  }

  const handleNext = () => {
    if (queueDetails && queueDetails.mixinfo && currentPairIndex < queueDetails.mixinfo.mix_pairs.length - 1) {
      const newIndex = currentPairIndex + 1
      setCurrentPairIndex(newIndex)
      // 转换为1-based URL
      navigate(`/experiments/${instance}/${queue}/${newIndex + 1}`, { replace: true })
    }
  }

  const handlePairSelect = (pairIndex: number) => {
    setCurrentPairIndex(pairIndex)
    // 转换为1-based URL
    navigate(`/experiments/${instance}/${queue}/${pairIndex + 1}`, { replace: true })
  }

  const handleJumpTo = () => {
    if (!queueDetails?.mixinfo) return
    
    const jumpIndex = parseInt(jumpToInput, 10) - 1 // 用户输入的是从1开始的编号
    if (!isNaN(jumpIndex) && jumpIndex >= 0 && jumpIndex < queueDetails.mixinfo.mix_pairs.length) {
      handlePairSelect(jumpIndex)
      setJumpToInput('')
    } else {
      alert(`请输入有效的Mix Pair编号 (1-${queueDetails.mixinfo.mix_pairs.length})`)
    }
  }

  const handleJumpInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleJumpTo()
    }
  }



  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">加载混音数据...</p>
        </div>
      </div>
    )
  }

  if (error || !queueDetails) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-8 text-center">
          <div className="text-red-600 dark:text-red-400 mb-2">加载失败</div>
          <div className="text-red-500 dark:text-red-300 text-sm mb-4">{error}</div>
          <Link
            to={`/experiments/${instance}`}
            className="inline-block text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
          >
            返回算法队列列表
          </Link>
        </div>
      </div>
    )
  }

  if (!queueDetails.mixinfo) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-8 text-center">
          <div className="text-yellow-600 dark:text-yellow-400 mb-2">无混音数据</div>
          <div className="text-yellow-500 dark:text-yellow-300 text-sm mb-4">该队列没有可用的混音信息</div>
          <Link
            to={`/experiments/${instance}`}
            className="inline-block text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
          >
            返回算法队列列表
          </Link>
        </div>
      </div>
    )
  }

  const currentPair = queueDetails.mixinfo.mix_pairs[currentPairIndex]

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link
                to={`/experiments/${instance}`}
                className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                返回算法列表
              </Link>
              <span className="text-gray-400 dark:text-gray-500">/</span>
              <span className="text-gray-900 dark:text-white font-medium">队列 {queue}</span>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* 对比日志按钮 */}
              <button
                onClick={() => navigate(`/experiments/${instance}/${queue}/${currentPairIndex + 1}/compare`)}
                className="px-3 py-1 text-sm bg-orange-600 hover:bg-orange-700 text-white rounded transition-colors"
              >
                对比日志
              </button>
              
              {/* 跳转到指定Mix Pair */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">跳转到:</span>
                <input
                  type="number"
                  min="1"
                  max={queueDetails.mixinfo!.mix_pairs.length}
                  value={jumpToInput}
                  onChange={(e) => setJumpToInput(e.target.value)}
                  onKeyPress={handleJumpInputKeyPress}
                  placeholder={`1-${queueDetails.mixinfo!.mix_pairs.length}`}
                  className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <button
                  onClick={handleJumpTo}
                  className="px-3 py-1 text-sm bg-primary-600 hover:bg-primary-700 text-white rounded transition-colors"
                >
                  跳转
                </button>
              </div>
              
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Mix Pair {currentPairIndex + 1} / {queueDetails.mixinfo!.mix_pairs.length}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Left Sidebar - Mix Pair Navigator */}
          <div className="lg:col-span-1">
            <MixPairNavigator
              mixPairs={queueDetails.mixinfo!.mix_pairs}
              currentPairIndex={currentPairIndex}
              onPairSelect={handlePairSelect}
            />
          </div>

          {/* Right Content */}
          <div className="lg:col-span-3 space-y-8">
            {/* Mix Pair Info */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          {/* Mix Pair Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Mix Pair {currentPair.pair_index + 1}
            </h2>
            <div className="text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full">
              {currentPairIndex + 1} of {queueDetails.mixinfo.mix_pairs.length}
            </div>
          </div>
          
          {/* Debug Status Display */}
          {(currentPair.debug_status !== undefined || currentPair.statusCode !== undefined) && (
            <div className="mb-6">
              <DebugStatus 
                debugStatus={currentPair.debug_status}
                debugMessage={currentPair.debug_msg}
                statusCode={currentPair.statusCode}
              />
            </div>
          )}
          
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Previous Track */}
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <h3 className="text-base font-medium text-gray-900 dark:text-white">Prev Track</h3>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                <div className="font-medium text-blue-900 dark:text-blue-100">{currentPair.track_prev.song}</div>
                <div className="text-blue-700 dark:text-blue-300 text-sm">{currentPair.track_prev.artist}</div>
                <div className="text-blue-600 dark:text-blue-400 text-xs mt-1 space-y-0.5">
                  <div>BPM: {currentPair.track_prev.bpm}</div>
                  <div>{currentPair.track_prev.hash}</div>
                  {currentPair.track_prev.duration_seconds && (
                    <div>时长: {formatTime(currentPair.track_prev.duration_seconds)}</div>
                  )}
                </div>
                {/* Sync Out 时间段可视化 */}
                <TimelineBar 
                  track={currentPair.track_prev} 
                  syncType="syncOut" 
                />
              </div>
            </div>

            {/* Next Track */}
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                <h3 className="text-base font-medium text-gray-900 dark:text-white">Next Track</h3>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
                <div className="font-medium text-purple-900 dark:text-purple-100">{currentPair.track_next.song}</div>
                <div className="text-purple-700 dark:text-purple-300 text-sm">{currentPair.track_next.artist}</div>
                <div className="text-purple-600 dark:text-purple-400 text-xs mt-1 space-y-0.5">
                  <div>BPM: {currentPair.track_next.bpm}</div>
                  <div>{currentPair.track_next.hash}</div>
                  {currentPair.track_next.duration_seconds && (
                    <div>时长: {formatTime(currentPair.track_next.duration_seconds)}</div>
                  )}
                </div>
                {/* Sync In 时间段可视化 */}
                <TimelineBar 
                  track={currentPair.track_next} 
                  syncType="syncIn" 
                />
              </div>
            </div>
          </div>


            </div>

            {/* MultiTrack Audio Player */}
            <MultiTrackMixPlayer 
              queueDetails={queueDetails as any}
              currentPairIndex={currentPairIndex}
              onPairChange={handlePairSelect}
            />

            {/* Mix Status and Technical Details */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-6">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Mix Status & Technical Details</h4>
                {/* Status Label - 优先使用新的statusCode，如果没有则回退到旧的mixtability逻辑 */}
                {currentPair.statusCode !== undefined ? (
                  <StatusLabel statusCode={currentPair.statusCode} />
                ) : (
                  currentPair.mixtability && currentPair.mixtability.overall && (
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      currentPair.mixtability.overall.pass 
                        ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200' 
                        : 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-200'
                    }`}>
                      {currentPair.mixtability.overall.pass ? '通过' : '未通过'}
                    </span>
                  )
                )}
              </div>
              
              {/* Technical Details */}
              <div className="space-y-4 mb-6">
                {/* Technical Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {/* Mix Situation - 1/2 width */}
                  <div className="md:col-span-2 bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                    <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">混音情境</div>
                    <div className="text-lg font-semibold text-gray-900 dark:text-white">{currentPair.mix_situation_name}</div>
                  </div>
                  
                  {/* Transition Beats & Automation Preset - 1/2 width total */}
                  <div className="md:col-span-1 bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                    <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">过渡节拍数</div>
                    <div className="text-lg font-semibold text-gray-900 dark:text-white">{currentPair.transition_nbeats}</div>
                  </div>
                  <div className="md:col-span-1 bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                    <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">自动化预设</div>
                    <div className="text-lg font-semibold text-gray-900 dark:text-white">{currentPair.automation_preset}</div>
                  </div>
                </div>
              </div>

              {/* Mixtability Display */}
              {currentPair.mixtability && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                  <MixtabilityDisplay mixtability={currentPair.mixtability} />
                </div>
              )}
            </div>

            {/* Navigation */}
            <div className="grid md:grid-cols-3 gap-4">
                              <button
                  onClick={handlePrevious}
                  disabled={currentPairIndex === 0}
                  className="flex items-center justify-center space-x-2 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <SkipBack className="h-4 w-4" />
                  <span>上一个Mix Pair</span>
                </button>
              
              <div className="flex items-center justify-center p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-200 dark:border-primary-800">
                <Music2 className="h-4 w-4 text-primary-600 mr-2" />
                <span className="text-primary-800 dark:text-primary-200 font-medium">
                  {currentPairIndex + 1} / {queueDetails.mixinfo!.mix_pairs.length}
                </span>
              </div>
              
                              <button
                  onClick={handleNext}
                  disabled={currentPairIndex === queueDetails.mixinfo!.mix_pairs.length - 1}
                  className="flex items-center justify-center space-x-2 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <span>下一个Mix Pair</span>
                  <SkipForward className="h-4 w-4" />
                </button>
            </div>
          </div>
        </div>
      </div>

    </div>
  )
}
